/**
 * Browser-side error/log reporter — ships client errors to a durable sink so
 * they survive the user closing the tab.
 *
 * WHY THIS EXISTS: the recurring "a user complains but we can't reproduce it"
 * problem is almost always environment-specific (a particular browser, OS,
 * viewport, locale, or a blocking extension). None of that is recoverable after
 * the fact unless it was captured at the moment of failure. This module snapshots
 * that environment and POSTs it — with the error and stack — to `/api/client-logs`,
 * which forwards it into the backend's structured, correlated log stream.
 *
 * Design constraints:
 * - Fire-and-forget, never throws, never blocks the UI.
 * - Uses `navigator.sendBeacon` so reports survive a page unload/navigation.
 * - Capped per page load and re-entrancy-guarded so a failing sink can't create
 *   an error→report→error feedback loop.
 * - Redacts obvious secrets (tokens, JWTs, emails) before anything leaves the
 *   browser.
 */

const SINK_URL = '/api/client-logs'
const SESSION_STORAGE_KEY = 'pa_obs_session'

// Bound the blast radius of a broken page: at most this many reports per load.
const MAX_REPORTS_PER_LOAD = 25
const MAX_MESSAGE_LEN = 4000
const MAX_STACK_LEN = 8000

let reportsSent = 0
let inFlight = false

export type ClientLogLevel = 'debug' | 'info' | 'warn' | 'error'

export interface ClientLogInput {
  level?: ClientLogLevel
  message: string
  /** Origin, e.g. "window.onerror", "unhandledrejection", "react-error-boundary". */
  source?: string
  errorName?: string
  stack?: string
  /** Extra fields (e.g. Next.js error digest) merged into the report. */
  extra?: Record<string, unknown>
}

/** Strip obvious secrets from a string before it leaves the browser. */
function redact(input: string | undefined): string | undefined {
  if (!input) return input
  return input
    .replace(/([?&#](?:access_?token|token|password|pwd|secret|api[_-]?key|code)=)[^&#\s]+/gi, '$1[redacted]')
    .replace(/bearer\s+[A-Za-z0-9._-]+/gi, 'bearer [redacted]')
    .replace(/eyJ[A-Za-z0-9._-]{10,}/g, '[jwt]')
    .replace(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi, '[email]')
}

/** Stable-per-tab id so multiple reports from one session can be grouped. */
function getSessionId(): string | undefined {
  try {
    let id = sessionStorage.getItem(SESSION_STORAGE_KEY)
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`
      sessionStorage.setItem(SESSION_STORAGE_KEY, id)
    }
    return id
  } catch {
    // Private mode / storage disabled — sessionless report is still useful.
    return undefined
  }
}

/** Snapshot the browser environment — the data needed to reproduce a bug. */
function collectContext(): Record<string, unknown> {
  const screen = typeof window !== 'undefined' ? window.screen : undefined
  return {
    url: redact(window.location.href),
    referrer: redact(document.referrer) || undefined,
    user_agent: navigator.userAgent,
    language: navigator.language,
    timezone: (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
      } catch {
        return undefined
      }
    })(),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: screen ? `${screen.width}x${screen.height}` : undefined,
    device_pixel_ratio: window.devicePixelRatio,
    orientation: screen?.orientation?.type,
    online: navigator.onLine,
    build_sha: process.env.NEXT_PUBLIC_BUILD_SHA,
    session_id: getSessionId(),
  }
}

/**
 * Report a client-side log/error. Safe to call from anywhere in the browser;
 * silently no-ops on the server, when the per-load cap is hit, or on any error.
 */
export function reportClientLog(input: ClientLogInput): void {
  if (typeof window === 'undefined') return
  // Re-entrancy guard: don't let the act of reporting (which touches the
  // network) trigger another report and loop.
  if (inFlight) return
  if (reportsSent >= MAX_REPORTS_PER_LOAD) return

  inFlight = true
  try {
    const entry = {
      level: input.level ?? 'error',
      message: (redact(input.message) ?? 'Unknown error').slice(0, MAX_MESSAGE_LEN),
      source: input.source,
      error_name: input.errorName,
      stack: redact(input.stack)?.slice(0, MAX_STACK_LEN),
      client_ts: Date.now(),
      context: { ...collectContext(), ...input.extra },
    }
    const body = JSON.stringify({ entries: [entry] })
    reportsSent += 1

    if (typeof navigator.sendBeacon === 'function') {
      // sendBeacon survives page unload; type must be JSON for the backend.
      navigator.sendBeacon(SINK_URL, new Blob([body], { type: 'application/json' }))
    } else {
      // keepalive lets the request outlive the page in fetch-only runtimes.
      void fetch(SINK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {})
    }
  } catch {
    // Never let telemetry break the app.
  } finally {
    inFlight = false
  }
}
